import React from 'react';
const { useState, useEffect, useMemo, useRef } = React;
import {
  ROLES, APTITUDES, DIAGNOSTIC_CONFIG, ROLEPLAY_HIDDEN_DATA, ROLEPLAY_SHEET_QUESTIONS, ROLEPLAY_TEXT_MAX,
  SESSION_STATUSES, EXIT_RISK_OPTIONS, MOTIVATORS_NAMED_1ON1, SESSION_NOTES_COUNT, SESSION_TEXT_MAX, TRANSCRIPT_LIMITS, t,
} from '../../diagnosticConfig.js';
import { cleanTranscript } from '../../diagnosticTranscript.js';
import { readTranscriptFile, TRANSCRIPT_FILE_ACCEPT, TRANSCRIPT_FILE_MAX_BYTES } from '../../diagnosticTranscriptFile.js';
import { adminCall, Field, inputSt, textareaSt, cardSt, labelSt, mutedSt, badgeSt, warnSt, fmtInt, fmtDate } from './shared.jsx';
import { FormAnswers, ConfidentialMotivation } from './PersonAnswers.jsx';

// Sesiones 1:1: per person, her answers (read-only), the session record, the
// role-play sheet, Ale's notes and preliminary scores, and the transcript.

const FILE_ERRORS = {
  unsupported: 'Solo se aceptan archivos .txt, .vtt o .docx (no audio).',
  too_large: `El archivo pesa más de ${TRANSCRIPT_FILE_MAX_BYTES / 1024 / 1024} MB.`,
  unreadable: 'No se pudo leer el archivo. Revisa que sea un .docx válido o pega el texto.',
};

const SESSION_LABEL = Object.fromEntries(SESSION_STATUSES.map(s => [s.key, t(s.label)]));

export default function SessionsPanel({ participants, run, onChanged }) {
  const people = participants.filter(p => p.forms.includes('self'));
  const [selected, setSelected] = useState(people[0]?.id || null);
  const [person, setPerson] = useState(null);

  const load = (id) => run(async () => { setPerson(null); setPerson(await adminCall({ action: 'getPerson', participantId: id })); });
  useEffect(() => { if (selected) load(selected); }, [selected]);

  // Every write returns the fresh person; the list (statuses) is refreshed too.
  // Resolves to true when it was saved.
  const save = async (body) => {
    let ok = false;
    await run(async () => {
      setPerson(await adminCall({ participantId: selected, ...body }));
      ok = true;
      await onChanged?.();
    });
    return ok;
  };

  if (!people.length) return <div style={{ ...cardSt, ...mutedSt }}>Todavía no hay personas con autoevaluación.</div>;

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {people.map(p => (
          <button key={p.id} className="action-btn" onClick={() => setSelected(p.id)}
            style={{ fontSize: 12, padding: '6px 10px', textAlign: 'left', ...(selected === p.id ? { borderColor: 'var(--globant-green)', color: 'var(--globant-green)' } : {}) }}>
            {p.name}
            <span style={{ ...mutedSt, fontSize: 10, display: 'block' }}>
              {p.session ? SESSION_LABEL[p.session.status] : 'Sin sesión'}{p.hasTranscript ? ' · 📝' : ''}
            </span>
          </button>
        ))}
      </div>
      {person && person.person.id === selected && <PersonDetail key={person.person.id + (person.session?.id || '')} data={person} save={save} />}
    </div>
  );
}

function PersonDetail({ data, save }) {
  const { person, answers, confidential } = data;
  const isManagerRole = person.forms.includes('manager');
  return (
    <div>
      <div style={{ ...cardSt, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{person.name}</div>
          <div style={mutedSt}>{t(ROLES[person.role]?.label) || person.role || 'Sin rol'}</div>
        </div>
        <div style={{ ...mutedSt, textAlign: 'right' }}>
          Motivadores con nombre en el formulario: <b>{person.motivatorsNamedForm ? 'sí' : 'no'}</b>
        </div>
      </div>

      <div style={{ ...labelSt, margin: '4px 0 8px' }}>Para preparar la 1:1</div>
      {!answers.self && <div style={{ ...cardSt, ...mutedSt }}>Todavía no empezó la autoevaluación.</div>}
      <FormAnswers formKey="self" record={answers.self} title="Autoevaluación (Partes 1, 2, 4 y 5)" />
      {answers.managerAboutPerson.map((r, i) => (
        <FormAnswers key={i} formKey="manager" record={r} sections={['person']} manager title={`Evaluación del manager${r.evaluator ? ` (${r.evaluator})` : ''}`} />
      ))}
      {isManagerRole && <FormAnswers formKey="manager" record={answers.managerTeam} sections={['team', 'teamProblems']} title="Su mirada sobre el equipo" />}
      <FormAnswers formKey="inventory" record={answers.inventory} title="Inventario de herramientas" />
      <ConfidentialMotivation block={confidential} />

      <div style={{ ...labelSt, margin: '20px 0 8px' }}>Registro de la 1:1</div>
      <SessionForm data={data} save={save} />
      <TranscriptCard transcript={data.transcript} save={save} />
    </div>
  );
}

// datetime-local works in local time; Airtable stores ISO (UTC)
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

function SessionForm({ data, save }) {
  const s = data.session || {};
  const formSaidNo = !data.person.motivatorsNamedForm;
  const [f, setF] = useState(() => ({
    sessionAt: toLocalInput(s.sessionAt), recorded: !!s.recorded, status: s.status || 'scheduled',
    exitRisk: s.exitRisk || '', motivatorsNamed1on1: data.person.motivatorsNamed1on1 || 'not_asked',
    notes: Array.from({ length: SESSION_NOTES_COUNT }, (_, i) => (s.notes || [])[i] || ''),
    sheet: { discovered: {}, ...(s.roleplaySheet || {}) },
    scores: Object.fromEntries(data.aptitudes.map(k => [k, { level: s.scores?.[k]?.level ?? '', evidence: s.scores?.[k]?.evidence || '' }])),
  }));
  const [dirty, setDirty] = useState(false);
  const upd = (patch) => { setF(x => ({ ...x, ...patch })); setDirty(true); };
  const setSheet = (patch) => upd({ sheet: { ...f.sheet, ...patch } });
  const setFound = (key, patch) => setSheet({ discovered: { ...f.sheet.discovered, [key]: { found: false, question: '', ...f.sheet.discovered[key], ...patch } } });
  const found = ROLEPLAY_HIDDEN_DATA.filter(d => f.sheet.discovered[d.key]?.found).length;
  // Nothing marked = not recorded (P1 ignores it). "Ninguno" records the 6 as not discovered: 0 of 6 is evidence.
  const hiddenRecorded = ROLEPLAY_HIDDEN_DATA.some(d => f.sheet.discovered[d.key]);
  const markNone = () => setSheet({ discovered: Object.fromEntries(ROLEPLAY_HIDDEN_DATA.map(d => [d.key, { found: false, question: f.sheet.discovered[d.key]?.question || '' }])) });
  const missingEvidence = data.aptitudes.filter(k => f.scores[k].level !== '' && !f.scores[k].evidence.trim());

  const selfLevels = data.answers.self?.answers?.aptitudes?.levels || {};
  const managerLevels = data.answers.managerAboutPerson.map(r => r.answers?.person?.levels || {});

  const submit = () => save({
    action: 'saveSession',
    sessionAt: f.sessionAt ? new Date(f.sessionAt).toISOString() : null,
    recorded: f.recorded, status: f.status, exitRisk: f.exitRisk || null,
    motivatorsNamed1on1: f.motivatorsNamed1on1,
    notes: f.notes,
    roleplaySheet: f.sheet,
    scores: Object.fromEntries(Object.entries(f.scores).map(([k, v]) => [k, { level: v.level === '' ? null : Number(v.level), evidence: v.evidence }])),
  });

  const sel = (value, onChange, options, empty) => (
    <select style={inputSt} value={value} onChange={e => onChange(e.target.value)}>
      {empty !== undefined && <option value="">{empty}</option>}
      {options.map(o => <option key={o.key} value={o.key}>{t(o.label)}</option>)}
    </select>
  );

  return (
    <div>
      <div style={cardSt}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <Field label="Fecha y hora"><input type="datetime-local" style={inputSt} value={f.sessionAt} onChange={e => upd({ sessionAt: e.target.value })} /></Field>
          <Field label="Estado">{sel(f.status, v => upd({ status: v }), SESSION_STATUSES)}</Field>
          <Field label="Grabación">
            <label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', paddingTop: 8 }}>
              <input type="checkbox" checked={f.recorded} onChange={e => upd({ recorded: e.target.checked })} /> Fue grabada
            </label>
          </Field>
          <Field label="Motivadores con nombre (1:1)" hint={formSaidNo ? 'En el formulario dijo que no: queda en no.' : 'Si cambia, gana la opción más restrictiva.'}>
            {sel(formSaidNo ? 'no' : f.motivatorsNamed1on1, v => upd({ motivatorsNamed1on1: v }),
              formSaidNo ? MOTIVATORS_NAMED_1ON1.filter(o => o.key !== 'yes') : MOTIVATORS_NAMED_1ON1)}
          </Field>
          <Field label="Riesgo de salida (nota de Ale)" hint="Nunca se muestra con nombre.">
            {sel(f.exitRisk, v => upd({ exitRisk: v }), EXIT_RISK_OPTIONS, 'Sin registrar')}
          </Field>
        </div>
      </div>

      <div style={cardSt}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={labelSt}>Hoja de registro del role-play · {hiddenRecorded ? `${found} de ${ROLEPLAY_HIDDEN_DATA.length} datos descubiertos` : 'datos escondidos sin registrar'}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="action-btn" style={{ fontSize: 11, padding: '3px 8px' }} onClick={markNone}
              title="Registra que no descubrió ninguno: 0 de 6 cuenta como evidencia">Ninguno</button>
            {hiddenRecorded && <button type="button" className="action-btn" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => setSheet({ discovered: {} })}
              title="Vuelve a 'sin registrar': P1 no lo toma como evidencia">Sin registrar</button>}
          </div>
        </div>
        <div style={{ ...mutedSt, marginBottom: 8 }}>Al marcar uno, los que quedan sin marcar cuentan como no descubiertos. Si no descubrió ninguno, usa "Ninguno".</div>
        {ROLEPLAY_HIDDEN_DATA.map(d => {
          const x = f.sheet.discovered[d.key] || {};
          return (
            <div key={d.key} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center', minWidth: 160 }}>
                <input type="checkbox" checked={!!x.found} onChange={e => setFound(d.key, { found: e.target.checked })} /> {t(d.label)}
              </label>
              <input style={{ ...inputSt, flex: '1 1 260px', width: 'auto' }} placeholder="¿Con qué pregunta lo descubrió?" maxLength={ROLEPLAY_TEXT_MAX}
                value={x.question || ''} onChange={e => setFound(d.key, { question: e.target.value })} />
            </div>
          );
        })}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginTop: 12 }}>
          {ROLEPLAY_SHEET_QUESTIONS.map(q => (
            <Field key={q.key} label={t(q.label)}>{sel(f.sheet[q.key] || '', v => setSheet({ [q.key]: v || undefined }), q.options, '—')}</Field>
          ))}
        </div>
      </div>

      <div style={cardSt}>
        <div style={{ ...labelSt, marginBottom: 4 }}>Observaciones de Ale</div>
        <div style={{ ...mutedSt, marginBottom: 10 }}>En lenguaje de observación: "en el role-play no preguntó por el impacto", nunca "es mala en discovery".</div>
        {f.notes.map((n, i) => (
          <textarea key={i} style={{ ...textareaSt, marginBottom: 8 }} placeholder={`Observación ${i + 1}`} maxLength={SESSION_TEXT_MAX}
            value={n} onChange={e => upd({ notes: f.notes.map((x, j) => (j === i ? e.target.value : x)) })} />
        ))}
      </div>

      <div style={cardSt}>
        <div style={{ ...labelSt, marginBottom: 4 }}>Puntajes preliminares de Ale</div>
        <div style={{ ...mutedSt, marginBottom: 10 }}>Solo las aptitudes que aplican según sus etapas. Cada puntaje lleva su evidencia.</div>
        {data.aptitudes.map(k => {
          const a = APTITUDES.find(x => x.key === k);
          const sc = f.scores[k];
          const others = [selfLevels[k] !== undefined && `Autoev.: ${selfLevels[k]}`,
            ...managerLevels.map(l => l[k] !== undefined && `Manager: ${l[k] === 'unknown' ? 'no sabe' : l[k]}`)].filter(Boolean).join(' · ');
          return (
            <div key={k} style={{ borderBottom: '1px solid var(--globant-border)', padding: '10px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{t(a.label)} <span style={{ ...mutedSt, fontWeight: 400 }}>· {t(a.evidence)}</span></div>
                {others && <div style={mutedSt}>{others}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <select style={{ ...inputSt, width: 210 }} value={sc.level}
                  onChange={e => upd({ scores: { ...f.scores, [k]: { ...sc, level: e.target.value } } })}>
                  <option value="">Sin puntaje</option>
                  {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n} · {t(DIAGNOSTIC_CONFIG.aptitudeScale[n])}</option>)}
                </select>
                <textarea style={{ ...textareaSt, flex: '1 1 280px', width: 'auto', minHeight: 38, ...(sc.level !== '' && !sc.evidence.trim() ? { borderColor: '#B4362C' } : {}) }}
                  placeholder="Evidencia: una frase, un dato o algo observado" maxLength={SESSION_TEXT_MAX}
                  value={sc.evidence} onChange={e => upd({ scores: { ...f.scores, [k]: { ...sc, evidence: e.target.value } } })} />
              </div>
              {sc.level !== '' && <div style={{ ...mutedSt, fontSize: 11, marginTop: 4 }}>{t(a.levels[sc.level])}</div>}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        {missingEvidence.length > 0 && <span style={{ fontSize: 12, color: '#B4362C' }}>Falta la evidencia de {missingEvidence.length} puntaje(s).</span>}
        <button className="action-btn btn-primary" disabled={!dirty || missingEvidence.length > 0} onClick={async () => { if (await submit()) setDirty(false); }}>
          💾 Guardar sesión
        </button>
      </div>
    </div>
  );
}

function TranscriptCard({ transcript, save }) {
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const [reading, setReading] = useState(false);
  const fileInput = useRef(null);
  const preview = useMemo(() => (text ? cleanTranscript(text) : ''), [text]);

  // The file is read here and only its text continues, exactly like pasted text.
  const chooseFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFileError(''); setReading(true);
    try {
      const content = await readTranscriptFile(file);
      if (!content.trim()) throw Object.assign(new Error('empty'), { code: 'unreadable' });
      setText(content); setFileName(file.name);
    } catch (err) {
      setFileError(FILE_ERRORS[err.code] || FILE_ERRORS.unreadable);
    }
    setReading(false);
  };
  const tooLong = preview.length > TRANSCRIPT_LIMITS.totalMax || text.length > TRANSCRIPT_LIMITS.rawMax;

  const upload = async () => {
    if (transcript && !window.confirm('Ya hay una transcripción cargada. ¿Reemplazarla?')) return;
    if (await save({ action: 'saveTranscript', text })) { setText(''); setFileName(''); }
  };
  const remove = () => {
    if (!window.confirm('¿Borrar la transcripción? No se puede deshacer.')) return;
    save({ action: 'deleteTranscript' });
  };

  return (
    <div style={cardSt}>
      <div style={{ ...labelSt, marginBottom: 8 }}>Transcripción</div>
      {transcript ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ fontSize: 13 }}>
            📝 {fmtInt(transcript.characters)} caracteres <span style={mutedSt}>(antes de limpiar: {fmtInt(transcript.originalCharacters)})</span>
            <div style={mutedSt}>Cargada el {fmtDate(transcript.uploadedAt, true)}{transcript.continued ? ' · usa Transcript (cont.)' : ''}</div>
          </div>
          <button className="action-btn" style={{ fontSize: 12, color: '#B4362C' }} onClick={remove}>🗑 Borrar transcripción</button>
        </div>
      ) : <div style={{ ...mutedSt, marginBottom: 10 }}>Sin transcripción. Pega el texto de Meet o Zoom, o elige el archivo (.txt, .vtt o .docx). Nunca el audio.</div>}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <input ref={fileInput} type="file" accept={TRANSCRIPT_FILE_ACCEPT} style={{ display: 'none' }} onChange={chooseFile} />
        <button className="action-btn" style={{ fontSize: 12 }} disabled={reading} onClick={() => fileInput.current?.click()}>
          {reading ? '⏳ Leyendo…' : '📄 Elegir archivo (.txt, .vtt, .docx)'}
        </button>
        {fileName && <span style={mutedSt}>Texto de {fileName}. El archivo no se sube ni se guarda.</span>}
      </div>
      {fileError && <div style={{ fontSize: 12, color: '#B4362C', marginBottom: 8 }}>{fileError}</div>}

      <textarea style={{ ...textareaSt, minHeight: 90 }} placeholder="Pega aquí la transcripción…" value={text}
        onChange={e => { setText(e.target.value); if (!e.target.value) setFileName(''); }} />
      {text && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, color: tooLong ? '#B4362C' : 'var(--globant-muted)' }}>
            {fmtInt(text.length)} caracteres → {fmtInt(preview.length)} después de limpiar
            {tooLong ? ` · supera el máximo de ${fmtInt(TRANSCRIPT_LIMITS.totalMax)}: recórtala antes de cargarla` : preview.length > TRANSCRIPT_LIMITS.fieldMax ? ' · se guarda en dos partes' : ''}
          </div>
          <button className="action-btn btn-primary" disabled={tooLong || !preview} onClick={upload}>
            {transcript ? 'Reemplazar transcripción' : 'Cargar transcripción'}
          </button>
        </div>
      )}
      {text.length > 0 && <div style={{ ...warnSt, marginTop: 10, marginBottom: 0, padding: 10, fontSize: 12 }}>
        Después de cargarla, borra el audio. La transcripción se borra 30 días después de entregar el informe.
      </div>}
    </div>
  );
}
